import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { withCustomComponent } from 'ui/components/withCustomComponent';
import { withRouter } from 'next/router';
import moment from 'moment';
import DropDownList from 'react-widgets/DropdownList';
import { scrollToAvailableTime } from 'ui/assets/js/application';
import {
  useResourceAccessRules,
  useResourceAccessTimeSlots,
} from '../../../env/hooks/useResourceAccessRules';
import { useResourceTimeSlotsQuery } from '../../../env/hooks/useResourceTimeSlots';
import { getDefaultTimesByDay } from '../../../env/utils/time/timeHelpers';
import { resourceSlotTimes } from '../../../env/config';

let instancesCount = 0;

export const TimeInputField = withCustomComponent('TimeInputField')(
  inject(
    'authStore',
    'bookingsStore',
  )(
    withRouter(
      observer(
        ({
          authStore,
          bookingsStore,
          value,
          name,
          onChange,
          errors,
          helpText,
          classes,
          containerClasses,
          id,
          label,
          dropUp,
          allowPastDates,
          resourceId,
        }) => {
          const [touched, setTouched] = useState(false);
          const [open, setOpen] = useState(false);
          const [divId, setDivId] = useState('');
          const [isLoadingData, setIsLoadingData] = useState(false);
          const { teams } = authStore;

          const resourceTimeSlotsQuery = useResourceTimeSlotsQuery(resourceId);
          const resourceTimeSlots = resourceTimeSlotsQuery.data ?? [];

          const selectedDate = useMemo(() => {
            const date = bookingsStore.booking?.FromTime;
            return date ? moment(new Date(date)) : moment();
          }, [bookingsStore.booking?.FromTime]);

          const defaultResourceTimeSlots = useMemo(() => {
            return getDefaultTimesByDay(selectedDate, resourceTimeSlots);
          }, [selectedDate, resourceTimeSlots]);

          const resourceAccessRuleQuery = useResourceAccessRules(resourceId);
          const resourceAccessRuleId = resourceAccessRuleQuery.data?.[0]?.Id;

          const resourceAccessTimeSlotsQuery =
            useResourceAccessTimeSlots(resourceAccessRuleId);

          const resourceAccessTeams = resourceAccessTimeSlotsQuery.data?.Teams;
          const isAccessRuleTeamMember = teams?.some((team) =>
            resourceAccessTeams?.includes(team.Id),
          );

          // Pick the access rule slot for the selected weekday (the rule has
          // one entry per day-of-week)
          const accessRuleTimes = useMemo(() => {
            return getDefaultTimesByDay(
              selectedDate,
              resourceAccessTimeSlotsQuery.data?.Timeslots,
            );
          }, [selectedDate, resourceAccessTimeSlotsQuery.data?.Timeslots]);

          useEffect(() => {
            if (
              !resourceAccessTimeSlotsQuery.isLoading &&
              !resourceAccessRuleQuery.isLoading
            ) {
              setIsLoadingData(false);
            }
          }, [
            resourceAccessTimeSlotsQuery.isLoading,
            resourceAccessRuleQuery.isLoading,
            isAccessRuleTeamMember,
          ]);

          useEffect(() => {
            instancesCount += 1;
            setDivId(`time-input-field-${instancesCount}`);
          }, []);

          useEffect(() => {
            authStore.loadTeams();
          }, []);

          // Pick the operating hours for the current booking date, in priority order:
          //   1. Team-specific access rule slot for this weekday (extended hours).
          //   2. Resource's own default slot for this weekday.
          //   3. Global resource fallback (covers resources with no per-day config).
          const activeTimeSlot =
            isAccessRuleTeamMember && accessRuleTimes
              ? accessRuleTimes
              : (defaultResourceTimeSlots ?? resourceSlotTimes);

          const getStartTime = () => {
            if (isLoadingData || !value) return null;

            return moment(value)
              .set({
                hour: activeTimeSlot.startHour,
                minute: activeTimeSlot.startMin,
              })
              .toDate();
          };

          const getEndTime = () => {
            if (isLoadingData || !value) return null;

            return moment(value)
              .set({
                hour: activeTimeSlot.endHour,
                minute: activeTimeSlot.endMin,
              })
              .toDate();
          };

          const getTimeSlots = () => {
            const startTime = getStartTime();
            const endTime = getEndTime();
            const currentDate = moment().toDate();
            let timeSlots = {
              all: [],
              past: [],
            };
            for (
              let tempDate = startTime;
              moment(tempDate) <= endTime;
              tempDate = moment(tempDate)
                .clone()
                .add(
                  typeof window !== 'undefined'
                    ? (window.bookingTimeStep ?? 30)
                    : 30,
                  'minutes',
                )
                .toDate()
            ) {
              const tempDateISO = moment(tempDate).toISOString();
              timeSlots.all.push(tempDateISO);
              if (tempDate < currentDate) {
                timeSlots.past.push(tempDateISO);
              }
            }
            return timeSlots;
          };

          const onDropdownChange = (dateISO) => {
            const date = moment(dateISO).toDate();
            setTouched(true);

            if (!value) {
              onChange(date, null);
              return;
            }
            let currentDate = value ?? new Date();
            let clone = moment(currentDate).clone().toDate();

            clone.setMinutes(date.getMinutes());
            clone.setHours(date.getHours());

            onChange(name, clone);
          };

          const onFocus = () => {
            setTouched(true);
          };

          const timeSlots = getTimeSlots().all;
          const pastTimeSlots = getTimeSlots().past;

          const hasErrors = errors && errors.length > 0;
          const timeIconElement = (
            <i className="icon-time fs-24 text-gray-600"></i>
          );

          const onToggle = () => {
            const isOpen = !open;
            setOpen(!open);
            if (isOpen) {
              scrollToAvailableTime(divId, pastTimeSlots.length);
            }
          };

          return (
            <div
              id={divId}
              className={`form-group mb-0 
              ${classes ? classes : ''}
            custom-timepicker time`}
            >
              {label && (
                <label className="control-label" htmlFor={id}>
                  {label}
                </label>
              )}
              <DropDownList
                id={id}
                open={open}
                onToggle={onToggle}
                dropUp={dropUp}
                data={timeSlots}
                disabled={allowPastDates ? [] : pastTimeSlots}
                textField={(value) => moment(value).format('LT')}
                value={value}
                busy={!timeSlots.length}
                selectIcon={timeIconElement}
                containerClassName={`form-group-sm 
                ${containerClasses ? containerClasses : ''}
              mb-0`}
                onChange={(val) => onDropdownChange(val)}
                onFocus={onFocus}
              />
              {hasErrors && (
                <div className="invalid-feedback">{errors.join(', ')}</div>
              )}
              {helpText && (
                <small class="form-text text-muted">{helpText}</small>
              )}
            </div>
          );
        },
      ),
    ),
  ),
);
